<template>
  <div class="tw:w-full" :style="containerStyle">
    <a-input
      v-model:value="inputValue"
      :class="inputClass"
      :style="inputStyle"
      v-bind="mergedAttrs"
      @change="handleChange"
      @pressEnter="handlePressEnter"
      @blur="handleBlur"
    >
      <template v-if="$slots.prefix" #prefix>
        <slot name="prefix" />
      </template>
      <template v-if="$slots.suffix" #suffix>
        <slot name="suffix" />
      </template>
    </a-input>
  </div>
</template>

<script setup lang="ts">
import { computed, normalizeClass, useAttrs, type HTMLAttributes } from 'vue'

interface Props {
  modelValue?: string
  placeholder?: string
  disabled?: boolean
  type?: string
  size?: 'large' | 'middle' | 'small'
  width?: string
  height?: string
  borderless?: boolean
  class?: HTMLAttributes['class']
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: '',
  disabled: false,
  type: 'text',
  size: 'middle',
  width: undefined,
  height: undefined,
  borderless: false,
  class: undefined,
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [e: Event]
  pressEnter: [e: KeyboardEvent]
  blur: [e: FocusEvent]
}>()

const attrs = useAttrs()

const inputValue = computed({
  get: () => props.modelValue,
  set: (val: string) => {
    emit('update:modelValue', val)
  },
})

const inputClass = computed(() => {
  return normalizeClass([
    'input-custom',
    { 'tw:bg-s-d-s-dark-background-opacity-100': props.borderless },
    props.class,
  ])
})

const containerStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.width) {
    style.width = props.width
  }
  return style
})

const inputStyle = computed(() => {
  const style: Record<string, string> = {}
  if (props.height) {
    style.height = props.height
  }
  return style
})

// Merge props với attrs, props có priority cao hơn
const mergedAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...restAttrs } = attrs
  return {
    ...restAttrs,
    placeholder: props.placeholder,
    disabled: props.disabled,
    type: props.type,
    size: props.size,
    bordered: !props.borderless,
  }
})

const handleChange = (e: Event) => {
  emit('change', e)
}

const handlePressEnter = (e: KeyboardEvent) => {
  emit('pressEnter', e)
}

const handleBlur = (e: FocusEvent) => {
  emit('blur', e)
}
</script>
